import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './termsOfUse.module.css';
import { getUser } from '../../data/selectors/userAuthSelectors';
import { agreeToTerms } from '../../services/auth';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';

export default function TermsOfUse() {
  const user = useSelector(getUser);
  let showAgreeBox = false;
  const dispatch = useDispatch();
  const [agreeCheckbox, setAgreeCheckbox] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'KNOW - Terms of Use';
  }, []);

  useEffect(() => {
    if (message === '') return;
    const messageTimeout = setTimeout(() => setMessage(''), 20000);
    return () => clearTimeout(messageTimeout);
  }, [message]);

  const handleAgree = () => setAgreeCheckbox(!agreeCheckbox);
  const submitAgreement = () => {
    if (agreeCheckbox) {
      setLoading(true);
      agreeToTerms(user._id)
        .then(({ data }) => {
          dispatch({ type: 'SET_USER', payload: data });
          setLoading(false);
          return navigate('/', { replace: true });
        })
        .catch((err) => console.error(err));
    } else {
      setMessage('Please accept the Terms of User by clicking the I Agree checkbox.');
    }
  };

  if (loading) return <LoadingSpinner />;

  if (user.email && !user.agreed_to_terms) showAgreeBox = true;

  return (
    <div className={styles.termsContainer} role="main" aria-label="terms of use">
      <section className={styles.termsSection}>
        {showAgreeBox && (
          <p className={`${styles.agreeHeading} h5`}>
            You must agree to these terms at the bottom of the page to continue using this website.
          </p>
        )}
        <h2 className={`${styles.termHeader} h4`}>Terms of Use</h2>
        <p>
          The Oncology Association of Naturopathic Physicians ("OncANP" or "we") provides the
          content posted on the KNOWoncology.org website and other related websites (the "KNOW
          Website") to you subject to the following Terms of Use. When you use the KNOW Website, you
          agree to be bound by these Terms of Use. These Terms of Use incorporate the KNOW Privacy
          Policy posted on the KNOW Website, as well as the terms of any subscriber agreement that
          may exist between you and KNOW. To the extent there is a conflict between these Terms of
          Use and your subscriber agreement, the terms of your subscriber agreement will control
          your use of the KNOW Website. Please read these Terms of Use carefully. If you do not
          agree with these Terms of Use, please do not use the KNOW Website.
        </p>
        <h2 className={styles.termHeader}>The KNOW Website</h2>
        <p>
          The KNOW Website, including the text, graphics, audio files and software that appear on
          the KNOW Website, is owned by or licensed to OncANP, and is subject to copyright and other
          intellectual property rights under United States, foreign laws and international
          conventions. Users may not re-post KNOW's content on other web sites without the
          permission of the OncANP. Users cannot re-sell KNOW's content to third parties, or prepare
          derivative works of KNOW's content.
        </p>
        <h2 className={styles.termHeader}>Patient Handouts</h2>
        <p>
          The KNOW Website is designed to offer users general health information for educational
          purposes only. The general health information furnished on this site is not intended to
          replace personal consultation with a qualified health care professional. You must always
          seek the advice of a professional for questions related to your health, disease, disease
          symptoms, and appropriate therapeutic treatments. If you have or suspect that you have a
          medical problem or condition, please contact a qualified health care professional
          immediately. You should never disregard medical advice or delay in seeking it because of
          something you have read on this site. IF YOU HAVE AN EMERGENCY, CALL 9-1-1. KNOW does not
          give medical advice, nor do we provide medical or diagnostic services. Neither KNOW nor
          any KNOW entity guarantees that the content covers all possible uses, direction,
          precautions, drug interactions, or adverse effects that may be associated with any
          therapeutic treatments. You may not rely on the application of any information on the KNOW
          Website as being applicable to your specific circumstances. Neither KNOW nor any KNOW
          Entity assumes any liability or responsibility for damages or injury to you, other
          persons, or property arising from any use of any information, idea, or instruction
          contained in the KNOW Website.
        </p>
        <h2 className={styles.termHeader}>Permission to reproduce KNOW material</h2>
        <p>
          Patient Handouts may be re-printed and shared. All other materials on KNOW are intended
          for personal use only. To use any materials other than Patient Handouts, you must receive
          permission to reproduce from KNOW in writing. To request permission to reproduce KNOW
          material please submit your request in writing via e-mail, fax, or regular mail. You may
          wish to include a copy of the material for which you are requesting permission. Your
          permission request must include the following information:
          <ul>
            <li>
              Title of the topic review or figure (figure titles can be found in the outline of each
              topic review)
            </li>
            <li>Title of the topic review in which a requested figure appears</li>
            <li>The format in which the material will be reproduced (e.g. print, CD, Internet)</li>
            <li>
              Title, publisher, author/editor, and publication date for the publication in which the
              figure and/or topic review will be reproduced.
            </li>
          </ul>
          Please send your permission request to: info@KNOWoncology.org
        </p>
        <h2 className={styles.termHeader}>Allowed Uses</h2>
        <p>
          KNOW encourages its users to read or print topic reviews and graphics for personal,
          clinical, educational, or research use. KNOW encourages its users to drag and drop
          Research Summaries and References to use in communication letters to other oncology
          professionals. We reserve the right to limit or suspend your use of the KNOW Website, by
          electronic self-help or other means and without notice, if we believe you have committed a
          breach of security or violated these Terms of Use, subject only to any terms in your
          subscriber agreement, as applicable. KNOW reserves all of its rights in the KNOW Website
          not expressly granted to you by these Terms of Use.
        </p>
        <h2 className={styles.termHeader}>Privacy</h2>
        <p>
          These Terms of Use incorporate the entirety of the KNOW Privacy Policy posted on the KNOW
          Website.
        </p>
        <h2 className={styles.termHeader}>No Unlawful Use</h2>
        <p>
          You agree not to use the KNOW Website for any purpose that is unlawful or prohibited by
          these terms, or the terms of a subscriber agreement you have with KNOW, if applicable. You
          may not use the KNOW Website in any manner that could damage, disable, overburden, or
          impair any KNOW server, or the network(s) connected to any KNOW server, or interfere with
          any other party's use and enjoyment of the KNOW Website. You may not attempt to gain
          unauthorized access to any portion of the KNOW Website, other accounts, computer systems,
          or networks connected to any KNOW server, through hacking, password or data mining, or any
          other means. You may not obtain or attempt to obtain any materials or information through
          any means not intentionally made available to you on the KNOW Website.
        </p>
        <h2 className={styles.termHeader}>No Warranties</h2>
        <p>
          NEITHER KNOW, NOR ANY PARENT, SUBSIDIARY, AFFILIATE, DIRECTOR, EMPLOYEE, LICENSOR,
          LICENSEE, DISTRIBUTOR, SUPPLIER, AGENT, OR RESELLER OF KNOW (COLLECTIVELY, THE "KNOW
          ENTITIES") MAKE ANY WARRANTY OR REPRESENTATION, EXPRESSED OR IMPLIED, WITH RESPECT TO THE
          KNOW SERVICES, WHICH ARE PROVIDED "AS IS", OR WITH RESPECT TO THESE TERMS OF USE. THE KNOW
          ENTITIES EXPRESSLY EXCLUDE AND DISCLAIM ALL WARRANTIES, INCLUDING, WITHOUT LIMITATION,
          IMPLIED WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE, AND ANY
          WARRANTIES ARISING BY STATUTE OR OTHERWISE IN LAW OR FROM COURSE OF DEALING, COURSE OF
          PERFORMANCE, OR USE OF TRADE. ANY STATEMENTS OR REPRESENTATIONS MADE BY ANY OTHER PERSON
          OR ENTITY ARE VOID. YOU ASSUME ALL RISK AS TO THE QUALITY, FUNCTION, AND PERFORMANCE OF
          THE KNOW WEBSITE. Some states or other jurisdictions do not allow the exclusion of implied
          warranties, so the above exclusions may not apply to you. In addition, please note that
          the KNOW Website may include technical inaccuracies or typographical errors. KNOW has the
          right at any time to change, modify, add to, discontinue, or retire any aspect or feature
          of the KNOW Website, including, but not limited to, hours of availability, equipment
          needed for access or use, or the availability of the KNOW Services on any particular
          device or communications service. KNOW has no obligation to provide you with notice of any
          such changes, and KNOW is under no obligation to provide you with any support, error
          corrections, updates, upgrades, bug fixes, and/or enhancements of the KNOW Website, except
          as provided by your subscriber agreement with KNOW, if applicable. Limitation of Liability
          IN NO EVENT WILL THE KNOW ENTITIES OR ANY OTHER PARTY WHO HAS BEEN INVOLVED IN THE
          CREATION, PRODUCTION, DISTRIBUTION, PROMOTION, OR MARKETING OF THE KNOW WEBSITE OR THESE
          TERMS OF USE BE LIABLE TO YOU OR ANY OTHER PARTY FOR ANY SPECIAL, INDIRECT, INCIDENTAL,
          RELIANCE, EXEMPLARY, OR CONSEQUENTIAL DAMAGES, INCLUDING, WITHOUT LIMITATION, LOSS OF DATA
          OR PROFITS, OR FOR INABILITY TO USE THE KNOW WEBSITE, EVEN IF THE KNOW ENTITIES OR SUCH
          OTHER PARTY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. SUCH LIMITATION SHALL
          APPLY NOTWITHSTANDING ANY FAILURE OF ESSENTIAL PURPOSE OF ANY LIMITED REMEDY AND TO THE
          FULLEST EXTENT PERMITTED BY LAW. Some states or other jurisdictions do not allow
          limitations on how long an implied warranty lasts and some jurisdictions do not allow the
          exclusion or limitation of special, indirect, incidental, exemplary, or consequential
          damages, or the limitation of liability to specified amounts, so the above limitation and
          exclusion may not apply to you if prohibited by applicable law.
        </p>
        <h2 className={styles.termHeader}>Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless the KNOW Entities from all liabilities,
          claims, and expenses, including attorneys' fees, that arise from your use of the KNOW
          Website. KNOW reserves the right, at it own expense, to assume the exclusive defense and
          control of any matter otherwise subject to indemnification by you, in which event you will
          cooperate with KNOW in asserting any available defenses.
        </p>
        <h2 className={styles.termHeader}>Modifications</h2>
        <p>
          KNOW may modify these Terms of Use from time to time and changes will be effective upon
          posting to the KNOW Website. You agree to be bound by any modification of these Terms of
          Use when you use the KNOW Website, effective after their posting to the KNOW Website. It
          is therefore important that you review these Terms of Use regularly to ensure you are
          updated as to any changes.
        </p>
        <h2 className={styles.termHeader}>Claim of Copyright Infringement</h2>
        <p>
          KNOW respects the intellectual property of others. If you believe that your copyrighted
          work has been copied and is accessible on the KNOW Website in a way that constitutes
          copyright infringement, pursuant to Title 17, United States Code, Section 512(c)(2), you
          may send a notification of claimed copyright infringement to KNOW's designated agent along
          with the following information:
          <ul>
            <li>
              the electronic or physical signature of the owner of the copyright or the person
              authorized to act on the owner's behalf
            </li>
            <li>
              a description of the copyrighted work that you claim has been infringed and a
              description of the infringing activity
            </li>
            <li>
              identification of the location where the original or an authorized copy of the
              copyrighted work exists, for example the URL of the website where it is posted or the
              name of the book in which it has been published
            </li>
            <li>
              identification of the URL or other specific location on this site where the material
              that you claim is infringing is located; you must include enough information to allow
              KNOW to locate the material
            </li>
            <li>your name, address, telephone number, and email address</li>
            <li>
              a statement by you that you have a good faith belief that the disputed use is not
              authorized by the copyright owner, its agent, or the law
            </li>
            <li>
              a statement by you, made under penalty of perjury, that the above information in your
              Notice is accurate and that you are the copyright owner or are authorized to act on
              the copyright owner's behalf
            </li>
          </ul>
          KNOW's designated agent for notice of claims oft copyright infringement on the KNOW
          Website can be reached as follows: By fax: 800-908-5175 By email: info@KNOWoncology.org
          Please note that this contact information is only for reporting copyright infringement and
          any other inquiries reported through this process will not be guaranteed a response.
        </p>
        <h2 className={styles.termHeader}>Trademarks</h2>
        <p className={styles.lastP}>
          All of the KNOW trademarks are owned by OncANP and may not be used for any purpose without
          the prior written permission of OncANP.
        </p>
        <br />
        {showAgreeBox && (
          <div className={styles.agreeSection}>
            <label>
              <input type="checkbox" value={agreeCheckbox} onClick={handleAgree} />I agree to these
              terms and conditions.
            </label>
            <button onClick={submitAgreement}>Submit Agreement</button>
            <p>{message}</p>
          </div>
        )}
      </section>
    </div>
  );
}
